// A minimal CRUD store the fake dialer API runs on: in memory (tests) or over a react-admin dataProvider (demo).
import type { DataProvider, Identifier } from "ra-core";

export interface RecordStore {
  list<T>(resource: string): Promise<T[]>;
  get<T>(resource: string, id: Identifier): Promise<T | null>;
  create<T>(resource: string, data: Record<string, unknown>): Promise<T>;
  update<T>(
    resource: string,
    id: Identifier,
    patch: Record<string, unknown>,
  ): Promise<T>;
  remove(resource: string, id: Identifier): Promise<void>;
}

type Row = Record<string, unknown> & { id: Identifier };

/** In-memory store for tests (immutable updates; callers get copies). */
export function memoryStore(
  seed: Record<string, Record<string, unknown>[]>,
): RecordStore {
  const db = new Map<string, Row[]>(
    Object.entries(seed).map(([k, rows]) => [
      k,
      rows.map((r) => ({ ...r }) as Row),
    ]),
  );
  const rows = (r: string) => db.get(r) ?? [];
  return {
    async list<T>(r: string) {
      return rows(r).map((x) => ({ ...x })) as T[];
    },
    async get<T>(r: string, id: Identifier) {
      const x = rows(r).find((y) => y.id === id);
      return (x ? { ...x } : null) as T | null;
    },
    async create<T>(r: string, data: Record<string, unknown>) {
      const id = rows(r).reduce((m, x) => Math.max(m, Number(x.id)), 0) + 1;
      const row = { ...data, id } as Row;
      db.set(r, [...rows(r), row]);
      return { ...row } as T;
    },
    async update<T>(r: string, id: Identifier, patch: Record<string, unknown>) {
      const next = rows(r).map((x) => (x.id === id ? { ...x, ...patch } : x));
      db.set(r, next);
      return { ...next.find((x) => x.id === id) } as T;
    },
    async remove(r: string, id: Identifier) {
      db.set(
        r,
        rows(r).filter((x) => x.id !== id),
      );
    },
  };
}

/** Adapter over a react-admin dataProvider (FakeRest demo mode shares the app's data). */
export function dataProviderStore(dp: DataProvider): RecordStore {
  return {
    async list<T>(r: string) {
      try {
        const { data } = await dp.getList(r, {
          pagination: { page: 1, perPage: 100_000 },
          sort: { field: "id", order: "ASC" },
          filter: {},
        });
        return data as T[];
      } catch {
        return [];
      }
    },
    async get<T>(r: string, id: Identifier) {
      try {
        return (await dp.getOne(r, { id })).data as T;
      } catch {
        return null;
      }
    },
    async create<T>(r: string, data: Record<string, unknown>) {
      return (await dp.create(r, { data })).data as T;
    },
    async update<T>(r: string, id: Identifier, patch: Record<string, unknown>) {
      const previousData = (await dp.getOne(r, { id })).data;
      return (await dp.update(r, { id, data: patch, previousData })).data as T;
    },
    async remove(r: string, id: Identifier) {
      const previousData = (await dp.getOne(r, { id })).data;
      await dp.delete(r, { id, previousData });
    },
  };
}
